/**
 * RedshiftForm — Redshift warehouse connection form (RHF-based, add-only).
 *
 * Two auth methods, chosen via the "Authentication method" toggle:
 * - Username & password (`managed_password`): you name a dedicated service user and
 *   set its password, and the form builds setup SQL (`CREATE USER … PASSWORD` +
 *   grants) for a Redshift superuser to run. The same password is stored as the
 *   connection credential.
 * - IAM role (`iam`): Lookout assumes the RudderStack IAM role and mints temporary
 *   database credentials. You provide the role ARN, cluster identifier, and region;
 *   the form builds passwordless-user setup SQL plus the IAM policy JSON (the AWS
 *   account ID is derived from the role ARN). No password is stored.
 *
 * Either way, account details (host/port/database) are prefilled from the RudderStack
 * destination and read-only when a synced value exists; any value the destination didn't
 * provide stays editable so the admin can fill it in. Outputs regenerate as fields change.
 *
 * `manualEntry` says the bucket was typed by hand (phase 1.5, AI-1370) — there is no synced
 * destination behind these values, so host and port must stay editable:
 *   - **port**: `redshiftPrefillFromRsConfig` always yields one, defaulting to 5439 when the
 *     config has none, so "a value is present" stops meaning "a destination supplied it" and
 *     the field would lock on the form's own default. A cluster on any other port is then
 *     unreachable.
 *   - **host**: a Redshift host dim is not always dialable. `deriveAccountKey` takes
 *     `host || clusterId || workgroupName`, step 2's field is labelled "Host or cluster id",
 *     and the server accepts a config host whose leading label is that dim
 *     (`assertRedshiftConfigMatchesBucketDims`). Freezing the field to the dim would strand a
 *     customer who identified the cluster rather than typing its endpoint.
 * The database stays read-only in both paths: the db dim has no such flavours — the server
 * requires the config's `database` to equal it exactly. Redshift is the only family with this
 * split, which is why no other form takes the prop.
 *
 * Pure field collector: it owns RHF state and exposes `validate()` + `getValues()`
 * via an imperative handle; Test/Save are driven by the parent (the drawer header).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  awsAccountIdFromRoleArn,
  DEFAULT_REDSHIFT_PORT,
  isAllowedRedshiftIamHost,
  isSafeSqlIdentifier,
  isValidAwsRegion,
  isValidIamRoleArn,
  isValidRedshiftClusterIdentifier,
  isValidRedshiftHost,
  REDSHIFT_IAM_HOST_VALIDATION_MESSAGE,
} from '@server/redshift';
import type { RedshiftConnectionConfig } from '@server/types';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { CopyButton } from '@/_shared/components/CopyButton';
import { ButtonGroup } from '@/_shared/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { generateRedshiftIamPolicyJSON, generateRedshiftIamSetupSQL } from '../../../lib/redshift-iam-setup';
import { generateRedshiftSetupSQL } from '../../../lib/redshift-setup';
import { DownloadButton } from '../shared/DownloadButton';
import {
  type FormDraft,
  useDraftDefaults,
  useResetOnChange,
  useWarehouseFormHandle,
  type WarehouseFormHandle,
} from '../shared/formHandle';
import { FormSection } from '../shared/FormSection';
import { HiddenFieldErrors } from '../shared/HiddenFieldErrors';
import { OutputSection } from '../shared/OutputSection';
import { joinClauses } from '../shared/utils';
import { RedshiftTrustPolicy } from './RedshiftTrustPolicy';

/** Auth methods offered by the toggle. Maps to RedshiftConnectionConfig['authType']. */
type RedshiftFormAuthType = 'managed_password' | 'iam';

interface RedshiftFormProps {
  /** Values typed before the add flow remounted the form for a new account (see `useDraftDefaults`). */
  draft?: FormDraft;
  /** The add flow names the account above the form (ACT2-1031), so its identity fields are not repeated here. */
  hideIdentity?: boolean;
  /** Prefilled config from the hook — account details + the Lookout-managed username default (and IAM binding when the destination uses IAM). */
  initialConfig?: RedshiftConnectionConfig;
  /** True when the bucket was typed by hand — no synced destination backs host/port. */
  manualEntry?: boolean;
  /** Fired whenever a field changes, so the parent can reset a stale test result. */
  onChange?: () => void;
}

/**
 * A generated output block (setup SQL / IAM policy JSON): either the rendered `value`,
 * or the `message` the OutputSection should show in its place — a blank-fields hint or
 * the generator's own rejection reason. One shape for both blocks so the JSX reads as a
 * single `'value' in result` check instead of parallel value/error/missing branches.
 */
type OutputResult = { value: string } | { message: string };

interface RedshiftFormValues {
  authType: RedshiftFormAuthType;
  user: string;
  password: string;
  schema: string;
  host: string;
  port: string;
  database: string;
  iamRoleArn: string;
  clusterIdentifier: string;
  clusterRegion: string;
}

/**
 * Host policy depends on a runtime flag (`WAREHOUSE_ALLOW_PRIVATE_HOSTS`, read from the
 * WorkspaceContext flag bag), so the schema is a factory rather than a module const —
 * mirroring the server's `createRedshiftConnectionConfigSchema(allowPrivate = false)`.
 * Same default: an unthreaded caller stays strict. `allowPrivate` reaches BOTH host checks,
 * exactly as `createRedshiftIamConnectionSchema` threads it — note it never relaxes the AWS
 * endpoint-suffix requirement, which `isAllowedRedshiftIamHost` applies on top.
 */
function createRedshiftFormSchema(allowPrivate = false) {
  return z
    .object({
      authType: z.enum(['managed_password', 'iam']),
      user: z.string().trim().min(1, 'Service username is required'),
      password: z.string(),
      schema: z
        .string()
        .refine(
          (v) => !v.trim() || isSafeSqlIdentifier(v.trim()),
          'Invalid namespace: use letters, numbers, and underscores; must start with a letter or underscore',
        ),
      host: z
        .string()
        .trim()
        .min(1, 'Host is required')
        // Wrapped, not passed by reference: zod hands the check only the value, so the options
        // argument would be undefined and `allowPrivate` would silently fall back to false.
        .refine((v) => isValidRedshiftHost(v, { allowPrivate }), 'Invalid Redshift host'),
      port: z
        .string()
        .trim()
        .refine((v) => {
          const n = Number(v);
          return Number.isInteger(n) && n >= 1 && n <= 65535;
        }, 'Port must be a number between 1 and 65535'),
      database: z.string().trim().min(1, 'Database is required'),
      iamRoleArn: z.string(),
      clusterIdentifier: z.string(),
      clusterRegion: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.authType === 'managed_password') {
        if (!values.password.trim()) {
          ctx.addIssue({ code: 'custom', path: ['password'], message: 'Password is required' });
        }
        return;
      }
      // IAM hosts must be AWS Redshift endpoints — the base `host` refine only proves the
      // value is a syntactically valid host, not an allowed AWS one (the server enforces both).
      if (values.host.trim() && !isAllowedRedshiftIamHost(values.host, { allowPrivate })) {
        ctx.addIssue({ code: 'custom', path: ['host'], message: REDSHIFT_IAM_HOST_VALIDATION_MESSAGE });
      }
      if (!values.iamRoleArn.trim()) {
        ctx.addIssue({ code: 'custom', path: ['iamRoleArn'], message: 'IAM role ARN is required' });
      } else if (!isValidIamRoleArn(values.iamRoleArn)) {
        ctx.addIssue({ code: 'custom', path: ['iamRoleArn'], message: 'Invalid IAM role ARN' });
      }
      if (!values.clusterIdentifier.trim()) {
        ctx.addIssue({ code: 'custom', path: ['clusterIdentifier'], message: 'Cluster identifier is required' });
      } else if (!isValidRedshiftClusterIdentifier(values.clusterIdentifier)) {
        ctx.addIssue({ code: 'custom', path: ['clusterIdentifier'], message: 'Invalid cluster identifier' });
      }
      if (!values.clusterRegion.trim()) {
        ctx.addIssue({ code: 'custom', path: ['clusterRegion'], message: 'Cluster region is required' });
      } else if (!isValidAwsRegion(values.clusterRegion)) {
        ctx.addIssue({ code: 'custom', path: ['clusterRegion'], message: 'Invalid AWS region' });
      }
    });
}

const REDSHIFT_HIDDEN_DATABASE = [{ name: 'database', label: 'Database' }] as const;
const REDSHIFT_HIDDEN_HOST_AND_DATABASE = [{ name: 'host', label: 'Host' }, ...REDSHIFT_HIDDEN_DATABASE] as const;

export const RedshiftForm = React.forwardRef<WarehouseFormHandle, RedshiftFormProps>(function RedshiftForm(
  { draft, initialConfig, manualEntry = false, onChange, hideIdentity = false },
  ref,
): React.JSX.Element {
  const iamPrefill = initialConfig?.authType === 'iam' ? initialConfig : null;
  const allowPrivate = useWorkspaceOrNull()?.allowsPrivateWarehouseHosts ?? false;
  const schema = React.useMemo(() => createRedshiftFormSchema(allowPrivate), [allowPrivate]);
  const { base, defaults } = useDraftDefaults<RedshiftFormValues>(
    {
      authType: iamPrefill ? 'iam' : 'managed_password',
      user: initialConfig?.user ?? '',
      password: '',
      schema: initialConfig?.schema ?? '',
      host: initialConfig?.host ?? '',
      port: String(initialConfig?.port ?? DEFAULT_REDSHIFT_PORT),
      database: initialConfig?.database ?? '',
      iamRoleArn: iamPrefill?.iamRoleArn ?? '',
      clusterIdentifier: iamPrefill?.clusterIdentifier ?? '',
      clusterRegion: iamPrefill?.clusterRegion ?? '',
    },
    draft,
  );
  const form = useForm<RedshiftFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: defaults,
  });

  const authType = form.watch('authType');
  const isIam = authType === 'iam';

  // Account details are read-only only when prefilled from the RudderStack destination.
  // With no synced value (a destination missing one of these), the field stays editable so the
  // admin can fill it in. Decided once from the initial prefill.
  //
  // A hand-typed bucket has no destination at all, so neither host nor port was "supplied" in
  // that sense — both seed from the bucket dims / the 5439 default and stay editable (see the
  // file header for why the database does not).
  const hostReadOnly = !manualEntry && Boolean(initialConfig?.host?.trim());
  const portReadOnly = !manualEntry && initialConfig?.port != null;
  const databaseReadOnly = Boolean(initialConfig?.database?.trim());
  // In the add flow the account named above already says the host — unless it named a cluster id or
  // workgroup (no dot), whose dialable endpoint still has to be typed here.
  const prefilledHost = initialConfig?.host?.trim() ?? '';
  const hideHost = hideIdentity && prefilledHost !== '' && (!manualEntry || prefilledHost.includes('.'));

  // Outputs regenerate automatically — watch only the value-driving fields (not the
  // whole form) so typing in unrelated fields doesn't re-tokenize the highlighted blocks.
  const [watchedUser, watchedPassword, watchedDatabase, watchedRoleArn, watchedClusterId, watchedRegion] = useWatch({
    control: form.control,
    name: ['user', 'password', 'database', 'iamRoleArn', 'clusterIdentifier', 'clusterRegion'],
  });

  // Setup SQL: managed embeds the password; IAM creates a passwordless user. Returns a
  // single discriminated result carrying either the generated value or the message the
  // OutputSection should show — a "what's still blank" hint, or the generator's actual
  // reason (e.g. an unsafe identifier in the read-only synced database name, which the
  // admin can't fix here but at least needs to know about).
  const setupResult = React.useMemo((): OutputResult => {
    const user = watchedUser.trim();
    const database = watchedDatabase.trim();
    const password = watchedPassword.trim();
    const missing: string[] = [];
    if (!user) missing.push('service username');
    if (!isIam && !password) missing.push('password');
    if (!database) missing.push('database');
    if (missing.length) return { message: `Enter ${joinClauses(missing)} to generate the setup SQL.` };
    try {
      const value = isIam
        ? generateRedshiftIamSetupSQL({ username: user, database, readWrite: false })
        : generateRedshiftSetupSQL({ username: user, password, database, readWrite: false });
      return { value };
    } catch (err) {
      return { message: err instanceof Error ? err.message : 'Enter valid identifiers to generate the setup SQL.' };
    }
  }, [isIam, watchedUser, watchedPassword, watchedDatabase]);

  // IAM policy JSON (IAM only). Same result shape as the setup SQL, so a filled-but-rejected
  // value (e.g. an underscore cluster id) surfaces the generator's reason rather than a
  // misleading "fields incomplete" message. null when not in IAM mode.
  const policyResult = React.useMemo((): OutputResult | null => {
    if (!isIam) return null;
    const roleArn = watchedRoleArn.trim();
    // The AWS account ID is inferred from the role ARN — it always belongs to the account
    // that owns the role — so it's never a separate field, just an input to the policy JSON.
    const accountId = awsAccountIdFromRoleArn(roleArn);
    // ARN present but malformed: the account ID can't be inferred. Surface the specific
    // "valid ARN" nudge before the generic blank-fields list (matches the role ARN field error).
    if (roleArn && !accountId) {
      return { message: 'Enter a valid IAM role ARN to generate the IAM policy JSON.' };
    }
    const missing: string[] = [];
    if (!roleArn) missing.push('IAM role ARN');
    if (!watchedClusterId.trim()) missing.push('cluster identifier');
    if (!watchedRegion.trim()) missing.push('cluster region');
    if (!watchedUser.trim()) missing.push('service username');
    if (!watchedDatabase.trim()) missing.push('database');
    if (missing.length) return { message: `Enter ${joinClauses(missing)} to generate the IAM policy JSON.` };
    try {
      const value = generateRedshiftIamPolicyJSON({
        region: watchedRegion.trim(),
        accountId,
        clusterIdentifier: watchedClusterId.trim(),
        dbUser: watchedUser.trim(),
        database: watchedDatabase.trim(),
      });
      return { value };
    } catch (err) {
      return {
        message: err instanceof Error ? err.message : 'Enter valid IAM policy parameters to generate the policy JSON.',
      };
    }
  }, [isIam, watchedRoleArn, watchedClusterId, watchedRegion, watchedUser, watchedDatabase]);

  const buildConfig = React.useCallback((values: RedshiftFormValues): RedshiftConnectionConfig => {
    const common = {
      host: values.host.trim(),
      port: Number(values.port) || DEFAULT_REDSHIFT_PORT,
      database: values.database.trim(),
      user: values.user.trim(),
    };

    if (values.authType === 'iam') {
      const config: RedshiftConnectionConfig = {
        authType: 'iam',
        ...common,
        clusterIdentifier: values.clusterIdentifier.trim(),
        clusterRegion: values.clusterRegion.trim(),
        iamRoleArn: values.iamRoleArn.trim(),
      };
      if (values.schema.trim()) config.schema = values.schema.trim();
      return config;
    }

    const config: RedshiftConnectionConfig = { authType: 'managed_password', ...common };
    if (values.password.trim()) config.password = values.password.trim();
    if (values.schema.trim()) config.schema = values.schema.trim();
    return config;
  }, []);

  useWarehouseFormHandle(ref, form, buildConfig, base);
  const syntaxStyle = useSyntaxStyle();
  // A passing test no longer reflects edited values — let the parent reset it.
  useResetOnChange(form, onChange);

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="grid gap-5" noValidate>
        {hideIdentity && (
          <HiddenFieldErrors
            control={form.control}
            fields={hideHost ? REDSHIFT_HIDDEN_HOST_AND_DATABASE : REDSHIFT_HIDDEN_DATABASE}
          />
        )}
        {/* 1. Authentication method — drives which credential fields appear below. */}
        <FormField
          control={form.control}
          name="authType"
          render={({ field }) => (
            <FormItem className="rounded-xl border border-grey-50 bg-grey-25 p-4">
              <FormLabel>Authentication method</FormLabel>
              <FormDescription>How Lookout authenticates to your Redshift cluster.</FormDescription>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="managed_password">Username &amp; password</SelectItem>
                  <SelectItem value="iam">IAM role</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="user"
          render={({ field }) => (
            <FormSection label="Service Username" description="The user Lookout signs in as. The setup SQL creates it.">
              <Input {...field} placeholder="lookout_service_user" />
            </FormSection>
          )}
        />

        {/* 3. Before Host: password → Password; IAM → Cluster identifier + region. */}
        {isIam ? (
          <>
            <FormField
              control={form.control}
              name="clusterIdentifier"
              render={({ field }) => (
                <FormSection label="Cluster identifier">
                  <Input {...field} autoComplete="off" placeholder="my-redshift-cluster" />
                </FormSection>
              )}
            />
            <FormField
              control={form.control}
              name="clusterRegion"
              render={({ field }) => (
                <FormSection label="Cluster region">
                  <Input {...field} autoComplete="off" placeholder="us-east-1" />
                </FormSection>
              )}
            />
          </>
        ) : (
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormSection label="Password" description="Stored encrypted. Also used in the setup SQL.">
                <Input
                  {...field}
                  type="password"
                  autoComplete="off"
                  placeholder="Enter a password for the service user"
                />
              </FormSection>
            )}
          />
        )}

        {/* 4. Host — read-only, prefilled from the RudderStack destination. */}
        {!hideHost && (
          <FormField
            control={form.control}
            name="host"
            render={({ field }) => (
              <FormSection label="Host">
                <Input
                  {...field}
                  readOnly={hostReadOnly}
                  aria-readonly={hostReadOnly || undefined}
                  placeholder="warehouse.example.redshift.amazonaws.com"
                />
              </FormSection>
            )}
          />
        )}

        {/* 5. After Host: password → Port (read-only, prefilled); IAM → role ARN. */}
        {isIam ? (
          <FormField
            control={form.control}
            name="iamRoleArn"
            render={({ field }) => (
              <FormSection label="IAM Role ARN" description="RudderStack's IAM role, which Lookout assumes.">
                <Input
                  {...field}
                  autoComplete="off"
                  placeholder="arn:aws:iam::123456789012:role/rudderstack-redshift"
                />
              </FormSection>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="port"
            render={({ field }) => (
              <FormSection label="Port">
                <Input
                  {...field}
                  readOnly={portReadOnly}
                  aria-readonly={portReadOnly || undefined}
                  placeholder="5439"
                />
              </FormSection>
            )}
          />
        )}

        {/* 6. Database — read-only, prefilled from the RudderStack destination. */}
        {!hideIdentity && (
          <FormField
            control={form.control}
            name="database"
            render={({ field }) => (
              <FormSection label="Database">
                <Input
                  {...field}
                  readOnly={databaseReadOnly}
                  aria-readonly={databaseReadOnly || undefined}
                  placeholder="warehouse_ginkgo"
                />
              </FormSection>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="schema"
          render={({ field }) => (
            <FormSection label="Namespace" optional description="Default schema for queries. Empty means public.">
              <Input {...field} placeholder="Schema for RudderStack tables (optional)" />
            </FormSection>
          )}
        />

        {'value' in setupResult ? (
          <OutputSection
            label="Setup SQL"
            description={
              isIam
                ? 'Requires a Redshift superuser. Creates a passwordless IAM user. Run in your Redshift cluster.'
                : 'Requires a Redshift superuser. Run in your Redshift cluster.'
            }
            action={
              <ButtonGroup>
                <DownloadButton
                  text={setupResult.value}
                  filename={isIam ? 'rudder-lookout-redshift-iam-setup.sql' : 'rudder-lookout-redshift-setup.sql'}
                />
                <CopyButton text={setupResult.value} label="Copy" copyLabel="Setup SQL" />
              </ButtonGroup>
            }
          >
            <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
              {setupResult.value}
            </SyntaxHighlighter>
          </OutputSection>
        ) : (
          <OutputSection label="Setup SQL" error>
            {setupResult.message}
          </OutputSection>
        )}

        {isIam &&
          policyResult &&
          ('value' in policyResult ? (
            <OutputSection
              label="IAM Policy JSON"
              description="Attach to the RudderStack IAM role."
              action={
                <ButtonGroup>
                  <DownloadButton text={policyResult.value} filename="rudder-lookout-redshift-iam-policy.json" />
                  <CopyButton text={policyResult.value} label="Copy" copyLabel="IAM policy JSON" />
                </ButtonGroup>
              }
            >
              <SyntaxHighlighter language="json" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
                {policyResult.value}
              </SyntaxHighlighter>
            </OutputSection>
          ) : (
            <OutputSection label="IAM Policy JSON" error>
              {policyResult.message}
            </OutputSection>
          ))}

        {/* ACT2-1124: in a sibling workspace, the role must accept the sibling id as the external ID. */}
        {isIam && <RedshiftTrustPolicy />}
      </form>
    </Form>
  );
});
